import React, { useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useCustomAlertStore } from "../../Store/store";
import { GlassmorphismContainer } from "../styled/shared/Glassmorphism";
import styled from "styled-components";

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const ModalContainer = styled(GlassmorphismContainer)`
  width: 400px;
  min-height: 200px;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  @media (max-width: 600px) {
    width: 90%;
    padding: 15px;
    border-radius: 5px;
    gap: 10px;
    margin-bottom: 20px;
  }
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${(props) => {
    switch (props.type) {
      case "success":
        return "rgba(76, 175, 80, 0.2)";
      case "error":
        return "rgba(244, 67, 54, 0.2)";
      case "warning":
        return "rgba(255, 152, 0, 0.2)";
      case "info":
        return "rgba(33, 150, 243, 0.2)";
      default:
        return "rgba(255, 152, 0, 0.2)";
    }
  }};
  border: 2px solid
    ${(props) => {
      switch (props.type) {
        case "success":
          return "#4CAF50";
        case "error":
          return "#F44336";
        case "warning":
          return "#FF9800";
        case "info":
          return "#2196F3";
        default:
          return "#FF9800";
      }
    }};
  @media (max-width: 600px) {
    width: 50px;
    height: 50px;
  }
`;

const Title = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  text-align: center;
  color: #fff;
  @media (max-width: 600px) {
    font-size: 18px;
  }
`;

const Message = styled.p`
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
  max-width: 300px;
  @media (max-width: 600px) {
    font-size: 12px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 10px;
  @media (max-width: 600px) {
    gap: 5px;
    margin-top: 5px;
  }
`;

const Button = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 80px;

  &.primary {
    background: ${(props) => {
      switch (props.type) {
        case "success":
          return "#4CAF50";
        case "error":
          return "#F44336";
        case "warning":
          return "#FF9800";
        case "info":
          return "#2196F3";
        default:
          return "#4CAF50";
      }
    }};
    color: white;

    &:hover {
      background: ${(props) => {
        switch (props.type) {
          case "success":
            return "#45a049";
          case "error":
            return "#d32f2f";
          case "warning":
            return "#f57c00";
          case "info":
            return "#1976d2";
          default:
            return "#45a049";
        }
      }};
      transform: translateY(-1px);
    }
  }

  &.secondary {
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(255, 255, 255, 0.2);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      color: white;
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
  @media (max-width: 600px) {
    padding: 8px 15px;
    font-size: 13px;
    min-width: 60px;
  }
`;

const getIcon = (type) => {
  switch (type) {
    case "success":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          fill="currentColor"
          viewBox="0 0 16 16"
        >
          <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
        </svg>
      );
    case "error":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          fill="currentColor"
          viewBox="0 0 16 16"
        >
          <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM8 4a.905.905 0 0 0-.9.995l.35 3.507a.552.552 0 0 0 1.1 0l.35-3.507A.905.905 0 0 0 8 4zm.002 6a1 1 0 1 0 0 2 1 1 0 0 0 0-2z" />
        </svg>
      );
    case "warning":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          fill="currentColor"
          viewBox="0 0 16 16"
        >
          <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5m.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2" />
        </svg>
      );
    case "info":
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          fill="currentColor"
          viewBox="0 0 16 16"
        >
          <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z" />
        </svg>
      );
    default:
      return (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          fill="currentColor"
          viewBox="0 0 16 16"
        >
          <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5m.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2" />
        </svg>
      );
  }
};

export default function AlertModal() {
  const {
    isCustomAlertOpen, // 모달 표시 상태
    setIsCustomAlertOpen, // 모달 표시 상태 설정

    alertTitle, // 알림 제목
    alertMessage, // 알림 내용
    alertType = "warning", // 알림 타입

    onConfirm, // 확인 콜백
    onCancel, // 취소 콜백

    showCancelButton = true, // 취소 버튼 표시 여부
    confirmText = "확인", // 확인 버튼 텍스트
    cancelText = "취소", // 취소 버튼 텍스트
  } = useCustomAlertStore();
  const navigate = useNavigate();

  const handleConfirm = useCallback(async () => {
    // 먼저 모달을 닫음
    setIsCustomAlertOpen(false);

    // 그 다음 콜백 실행
    if (onConfirm) {
      await onConfirm();
    }
  }, [onConfirm, setIsCustomAlertOpen]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    }
    setIsCustomAlertOpen(false);
  }, [onCancel, setIsCustomAlertOpen]);

  const handleOverlayClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        handleCancel();
      }
    },
    [handleCancel],
  );

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isCustomAlertOpen) return;

      if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        handleConfirm();
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        handleCancel();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [isCustomAlertOpen, handleConfirm, handleCancel]);

  if (!isCustomAlertOpen) return null;

  return (
    <ModalOverlay onClick={handleOverlayClick}>
      <ModalContainer>
        <IconContainer type={alertType}>{getIcon(alertType)}</IconContainer>

        <Title>{alertTitle || "알림"}</Title>
        <Message>{alertMessage || "메시지가 없습니다."}</Message>

        <ButtonContainer>
          {showCancelButton && (
            <Button className="secondary" onClick={handleCancel}>
              {cancelText}
            </Button>
          )}
          <Button className="primary" type={alertType} onClick={handleConfirm}>
            {confirmText}
          </Button>
        </ButtonContainer>
      </ModalContainer>
    </ModalOverlay>
  );
}
